<template>
  <div>
    <template v-if="deviceLoading">
      <coozzy-spinner />
    </template>
    <div v-show="!deviceLoading">
      <div
        v-if="devices.length === 0"
        class="row mt-3">
        <div class="col-12">
          <h6>
            {{ $t('message.nonExistent.device') }}
          </h6>
        </div>
      </div>
      <b-tabs
        v-else
        v-model="activeTab"
        class="mt-3">
        <!-- Room-specific tabs -->
        <b-tab
          v-for="([room, devicesList]) in roomsWithDevices"
          :key="room.id"
          :title="room.name">
          <template>
            <div
              v-for="(device, deviceIndex) in devicesList"
              :id="`device-${target}-${room.id}-${device.id || device.internalId}`"
              :key="`device-${target}-${room.id}-${device.id || device.internalId}`"
              class="row">
              <device
                :key="`device-${target}-${room.id}-${device.id || device.internalId}`"
                :ref="`device-${target}-${room.id}-${device.id || device.internalId}`"
                :index="deviceIndex"
                :target="target"
                :render-devices="renderDevices"
                :read-only="readOnly"
                :device="device"
                :expand-by-default="'isNewItem' in device"
                :add-new-document="device.id && deviceIdNewDocument !== '' && deviceIdNewDocument === device.id"
                :owner-id="ownerId"
                @duplicateDevice="$emit('duplicateDevice', $event)"
                @roomChanged="handleRoomChange" />
            </div>
          </template>
        </b-tab>

        <!-- Unassigned Devices tab -->
        <b-tab
          v-if="getUnassignedDevices().length > 0"
          :title="$t('message.onBoarding.devices.unassignedDevices')">
          <template>
            <div
              v-for="(device, deviceIndex) in getUnassignedDevices()"
              :id="`device-${target}-unassigned-${device.id || device.internalId}`"
              :key="`device-${target}-unassigned-${device.id || device.internalId}`"
              class="row">
              <device
                :key="`device-${target}-unassigned-${device.id || device.internalId}`"
                :ref="`device-unassigned-${device.id || device.internalId}`"
                :index="deviceIndex"
                :target="target"
                :render-devices="renderDevices"
                :read-only="readOnly"
                :device="device"
                :expand-by-default="'isNewItem' in device"
                :add-new-document="device.id && deviceIdNewDocument !== '' && deviceIdNewDocument === device.id"
                :owner-id="ownerId"
                @duplicateDevice="$emit('duplicateDevice', $event)"
                @roomChanged="handleRoomChange" />
            </div>
          </template>
        </b-tab>
      </b-tabs>
    </div>

    <div class="row mt-3">
      <div class="col-2 mt-1">
        <coozzy-button
          class="w-100"
          design="prop-green"
          @click="handleAddDevice">
          {{ $t('message.onBoarding.devices.add') }}
        </coozzy-button>
      </div>
    </div>
  </div>
</template>

<script>
import { onboarding } from '@/mixins/onboarding'
import Device from '@/building/tabs/components/Device'
import CoozzyButton from '@/framework/components/button/CoozzyButton'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner'

export default {
  name: 'DevicesByRoomTab',
  components: {
    CoozzySpinner,
    Device,
    CoozzyButton
  },
  mixins: [onboarding],
  props: {
    target: {
      type: String,
      required: true
    },
    devices: {
      type: Array,
      required: true
    },
    rooms: {
      type: Array,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    renderDevices: {
      type: Boolean,
      default: true
    },
    deviceLoading: {
      type: Boolean,
      default: false
    },
    ownerId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      activeTab: 0,
      activeTabKey: null,
      isRestoringTab: false,
      deviceIdNewDocument: '',
      prevDevicesLength: 0
    }
  },
  computed: {
    roomsWithDevices() {
      return this.rooms
        .map(room => [room, this.sortedDevices.filter(device => device.roomId === room.id)])
        .filter(([room, devicesList]) => devicesList.length > 0)
    },
    visibleRooms() {
      return this.rooms.filter(room => {
        const devicesInRoom = this.getDevicesForRoom(room.id)
        return devicesInRoom.length > 0 || devicesInRoom.some(device => device.isNewItem)
      })
    },
    sortedDevices() {
      return [...this.devices].sort((a, b) => {
        if (a.uninstallation !== null && b.uninstallation === null) return 1
        if (a.uninstallation === null && b.uninstallation !== null) return -1

        if (a.order && b.order) {
          return a.order - b.order
        }
        if (!a.order && b.order) return 1
        if (a.order && !b.order) return -1

        if (a.isNewItem && !b.isNewItem) return 1
        if (!a.isNewItem && b.isNewItem) return -1

        if (a.isNewItem && b.isNewItem) {
          return b.numericId - a.numericId
        }

        return a.numericId - b.numericId
      })
    }
  },
  watch: {
    deviceLoading: {
      handler(newValue, oldValue) {
        if (oldValue === true && newValue === false) {
          this.$nextTick(() => {
            this.restoreActiveTab()
            const hasUnassigned = this.getUnassignedDevices().length > 0
            const totalTabs = this.roomsWithDevices.length + (hasUnassigned ? 1 : 0)
            const keyedTab = this.getTabIndexForKey(this.activeTabKey)

            if (totalTabs === 0) {
              this.activeTab = 0
              return
            }

            if (keyedTab !== null) {
              this.activeTab = keyedTab
            } else if (this.activeTab < 0) {
              this.activeTab = 0
            } else if (this.activeTab > totalTabs - 1) {
              this.activeTab = totalTabs - 1
            }
          })
        }
      },
      immediate: true
    },
    activeTab: {
      handler() {
        if (!this.isRestoringTab) {
          this.updateActiveTabKey()
        }
      }
    },
    activeTabKey: {
      handler(newValue) {
        this.persistActiveTabKey(newValue)
      }
    },
    rooms: {
      handler() {
        this.$nextTick(() => {
          this.restoreActiveTab()
        })
      },
      immediate: true
    },
    devices: {
      handler(newDevices, oldDevices) {
        this.$nextTick(() => {
          this.restoreActiveTab()
        })
        const previousLength = this.prevDevicesLength || 0
        this.prevDevicesLength = newDevices.length

        // Only handle new device assignment for actually NEW devices (have internalId but no id)
        // Don't auto-assign roomId for devices loaded from API (have id but empty roomId)
        if (newDevices.length > previousLength && oldDevices) {
          const newDevice = newDevices[newDevices.length - 1]
          // Only auto-assign if it's a truly new device (has internalId but no id)
          // This means it was just created, not loaded from API
          if (newDevice && newDevice.internalId && !newDevice.id && (!newDevice.roomId || newDevice.roomId === '')) {
            this.handleNewDeviceAssignment(newDevice)
          }
        }
      },
      immediate: false // Changed to false to prevent running on initial load
    }
  },
  mounted() {
    if (localStorage.getItem('deviceIdNewDocument')) {
      this.deviceIdNewDocument = localStorage.getItem('deviceIdNewDocument')
    }
  },
  methods: {
    getReferenceId() {
      return this.rooms?.[0]?.referenceId ||
        this.devices?.[0]?.referenceId ||
        this.$route?.params?.id ||
        this.ownerId ||
        ''
    },
    getStorageKey() {
      const referenceId = this.getReferenceId()
      if (!referenceId) return null
      return `devices-by-room-tab:${this.target}:${referenceId}`
    },
    persistActiveTabKey(tabKey) {
      const storageKey = this.getStorageKey()
      if (!storageKey || !tabKey) return
      localStorage.setItem(storageKey, tabKey)
    },
    restoreActiveTab() {
      const storageKey = this.getStorageKey()
      if (!storageKey) return

      const storedTabKey = localStorage.getItem(storageKey)
      if (storedTabKey) {
        this.activeTabKey = storedTabKey
      }

      const keyedTab = this.getTabIndexForKey(this.activeTabKey)
      if (keyedTab !== null && keyedTab !== this.activeTab) {
        this.isRestoringTab = true
        this.activeTab = keyedTab
        this.$nextTick(() => {
          this.isRestoringTab = false
        })
      }
    },
    getTabIndexForKey(tabKey) {
      if (!tabKey) return null

      if (tabKey === 'unassigned') {
        return this.getUnassignedDevices().length > 0 ? this.roomsWithDevices.length : null
      }

      const roomIndex = this.roomsWithDevices.findIndex(([room]) => room.id === tabKey)
      return roomIndex !== -1 ? roomIndex : null
    },
    updateActiveTabKey() {
      const hasUnassigned = this.getUnassignedDevices().length > 0
      const hasTabs = this.roomsWithDevices.length > 0 || hasUnassigned

      if (!hasTabs) {
        return
      }

      if (hasUnassigned && this.activeTab === this.roomsWithDevices.length) {
        this.activeTabKey = 'unassigned'
        return
      }

      const roomEntry = this.roomsWithDevices[this.activeTab]
      this.activeTabKey = roomEntry ? roomEntry[0].id : null
    },
    getRoomTitle(room) {
      return `${room.name}`
    },
    getDevicesForRoom(roomId) {
      return this.sortedDevices.filter(device => device.roomId === roomId)
    },
    getUnassignedDevices() {
      return this.sortedDevices.filter(device => !device.roomId || device.roomId === '' || !this.rooms.some(room => room.id === device.roomId))
    },
    handleRoomChange({ device, newRoomId, wasOpen }) {
      setTimeout(() => {
        if (!newRoomId || newRoomId === '') {
          this.activeTab = this.visibleRooms.length
        } else {
          const newRoomIndex = this.visibleRooms.findIndex(room => room.id === newRoomId)
          if (newRoomIndex !== -1) {
            this.activeTab = newRoomIndex
          }
        }

        this.$nextTick(() => {
          const isNewDevice = !device.id && device.internalId
          if (isNewDevice) {
            this.$root.$once('bv::tab::shown', () => {
              setTimeout(() => {
                if (wasOpen) {
                  const collapseId = `collapse-device-${this.target}-${newRoomId || 'unassigned'}-${device.internalId}`
                  this.$root.$emit('bv::toggle::collapse', collapseId)
                }
              }, 50)
            })
          } else if (wasOpen) {
            const collapseId = `collapse-device-${this.target}-${newRoomId || 'unassigned'}-${device.id}`
            this.$root.$emit('bv::toggle::collapse', collapseId)
          }

          const domRect = this.$el.querySelector(`#device-${this.target}-${newRoomId || 'unassigned'}-${device.id || device.internalId}`)
          domRect?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        })
      }, 0)
    },
    handleAddDevice() {
      const selectedRoom = this.activeTab < this.roomsWithDevices.length ? this.rooms[this.activeTab] : null
      this.$emit('addDevice', selectedRoom)
    },
    handleNewDeviceAssignment(newDevice) {
      const shouldAssignToSelectedRoom = this.activeTab < this.roomsWithDevices.length && this.activeTab >= 0

      if (shouldAssignToSelectedRoom) {
        const selectedRoom = this.rooms[this.activeTab]
        if (selectedRoom) {
          newDevice.roomId = selectedRoom.id
          // Use the correct action based on target (building vs object)
          if (this.target === 'building') {
            this.$store.dispatch('onboarding/updateBuildingDevice', newDevice)
          } else {
            this.$store.dispatch('onboarding/updateObjectDevice', newDevice)
          }
          this.scrollToDevice(newDevice, selectedRoom.id)
        }
      } else {
        this.activeTab = this.roomsWithDevices.length
        this.scrollToDevice(newDevice)
      }
    },
    scrollToDevice(device, roomId) {
      if (roomId) {
        this.$nextTick(() => {
          const domRect = this.$el.querySelector(`#device-${this.target}-${roomId}-${device.id || device.internalId}`)
          domRect?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        })
      } else {
        this.$nextTick(() => {
          const domRect = this.$el.querySelector(`#device-${this.target}-unassigned-${device.id || device.internalId}`)
          domRect?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        })
      }
    }
  }
}
</script>
